// Il passo «Notifica» di un'attività (tela, artboard 45): i canali con cui il pop avvisa, ognuno
// una connessione Gmail o Pushover con i suoi destinatari ammessi. Per Gmail gli indirizzi si
// scrivono come tag (Invio, virgola o spazio); per Pushover si scelgono fra i destinatari della
// connessione. Fuori elenco l'invio lo rifiuta il server.
import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { RoutineNotify, ToolGroup } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Select } from '../../components/Select';
import { PushStyle } from './PushStyle';

const EMAIL = /^[^@\s<>,;"]+@[^@\s<>,;"]+\.[^@\s<>,;"]+$/;
const KIND_LABEL: Record<string, string> = {
  gmail: 'Email · send_email',
  pushover: 'Push · send_push e send_alert_push',
};

export function StepNotify({
  value,
  onChange,
  groups,
}: {
  value: RoutineNotify[];
  onChange: (v: RoutineNotify[]) => void;
  groups: ToolGroup[];
}) {
  const channels = groups.filter((g) => g.kind === 'gmail' || g.kind === 'pushover');
  const free = channels.filter((g) => !value.some((n) => n.connection === g.id));
  const set = (i: number, n: RoutineNotify) => onChange(value.map((x, j) => (j === i ? n : x)));

  if (!channels.length)
    return (
      <Banner kind="info">
        Nessun canale collegato: aggiungi Gmail o Pushover in Strumenti. Senza canali il risultato
        resta nella sessione e nelle notifiche di PopTown.
      </Banner>
    );

  return (
    <>
      <p className="t-small dim" style={{ margin: 0 }}>
        Come avvisa il pop quando c'è qualcosa da dire. Ogni canale ha i suoi destinatari ammessi:
        puoi metterne quanti vuoi. Senza canali il risultato resta nella sessione e nelle notifiche
        di PopTown.
      </p>
      {value.map((n, i) => {
        const g = channels.find((c) => c.id === n.connection);
        return (
          <div
            key={n.connection}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-lg)',
              background: 'var(--panel-2)',
              padding: 14,
              display: 'grid',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select
                  label="Strumento del canale"
                  value={n.connection}
                  width="100%"
                  onChange={(id) => set(i, { connection: id, recipients: [] })}
                  options={channels
                    .filter((c) => c.id === n.connection || free.includes(c))
                    .map((c) => ({ value: c.id, label: `${c.name} · ${KIND_LABEL[c.kind]}` }))}
                />
              </div>
              <Button
                variant="icon"
                icon="trash"
                aria-label={`Togli il canale ${g?.name ?? n.connection}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              />
            </div>
            {g?.kind === 'pushover' ? (
              <>
                <PushChoice
                  group={g}
                  value={n.recipients}
                  onChange={(r) => set(i, { ...n, recipients: r })}
                />
                <PushStyle value={n} onChange={(v) => set(i, v)} />
              </>
            ) : (
              <EmailTags value={n.recipients} onChange={(r) => set(i, { ...n, recipients: r })} />
            )}
          </div>
        );
      })}
      {free.length > 0 && value.length < 10 && (
        <div>
          <Button
            variant="secondary"
            icon="plus"
            onClick={() => onChange([...value, { connection: free[0].id, recipients: [] }])}
          >
            Aggiungi canale
          </Button>
        </div>
      )}
      {value.length > 0 && (
        <Banner kind="info">
          Il canale dà al pop lo strumento d'invio anche se in Strumenti è spento, ma solo verso
          questi destinatari e solo in questa attività. Il PM sa dal contesto del giro come e a chi
          notificare.
        </Banner>
      )}
    </>
  );
}

/** Gli indirizzi come tag: Invio, virgola, spazio o uscita dal campo li aggiungono. */
function EmailTags({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState('');
  const [bad, setBad] = useState<string | null>(null);
  const commit = () => {
    const parts = text.split(/[\s,;]+/).filter(Boolean);
    if (!parts.length) return;
    const wrong = parts.filter((p) => !EMAIL.test(p));
    const good = parts.filter(
      (p) => EMAIL.test(p) && !value.some((v) => v.toLowerCase() === p.toLowerCase()),
    );
    if (good.length) onChange([...value, ...good]);
    setBad(wrong.length ? `Non è un indirizzo email: ${wrong.join(', ')}` : null);
    setText(wrong.join(' '));
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === ';') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span className="t-small" style={{ fontWeight: 600 }}>
        Destinatari ammessi
      </span>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
          minHeight: 40,
          padding: '5px 6px',
          border: `1px solid ${bad ? 'var(--danger)' : 'var(--border-strong)'}`,
          borderRadius: 'var(--r-md)',
          background: 'var(--panel)',
        }}
      >
        {value.map((v) => (
          <span
            key={v}
            className="t-small"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              height: 28,
              padding: '0 4px 0 10px',
              borderRadius: 'var(--r-full)',
              background: 'var(--accent-soft)',
              color: 'var(--text)',
            }}
          >
            {v}
            <button
              type="button"
              aria-label={`Togli ${v}`}
              onClick={() => onChange(value.filter((x) => x !== v))}
              style={{
                width: 22,
                height: 22,
                border: 'none',
                background: 'none',
                color: 'var(--dim)',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                cursor: 'pointer',
              }}
            >
              <Icon name="x" size={14} />
            </button>
          </span>
        ))}
        <input
          aria-label="Aggiungi un indirizzo"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onBlur={commit}
          placeholder={value.length ? '' : 'Scrivi un indirizzo e premi Invio'}
          style={{
            flex: 1,
            minWidth: 200,
            height: 28,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'var(--text)',
            font: '400 13px/18px var(--font-ui)',
          }}
        />
      </div>
      <span className="t-caption" style={{ color: bad ? 'var(--danger)' : 'var(--dim)' }}>
        {bad ??
          "Invio, virgola o spazio fanno il tag. Un indirizzo fuori elenco: l'invio si rifiuta."}
      </span>
    </div>
  );
}

/** I destinatari Pushover: quelli della connessione, da scegliere. */
function PushChoice({
  group,
  value,
  onChange,
}: {
  group: ToolGroup;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const names = (group.recipients ?? []).map((r) => r.name);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span className="t-small" style={{ fontWeight: 600 }}>
        Destinatari ammessi
      </span>
      <div
        role="group"
        aria-label="Destinatari Pushover"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
      >
        {names.map((name) => {
          const on = value.includes(name);
          return (
            <button
              key={name}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(on ? value.filter((x) => x !== name) : [...value, name])}
              style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 'var(--r-full)',
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`,
                background: on ? 'var(--accent-soft)' : 'transparent',
                color: on ? 'var(--accent)' : 'var(--text)',
                font: '500 13px/18px var(--font-ui)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
              }}
            >
              {on && <Icon name="check" size={14} />}
              {name}
            </button>
          );
        })}
      </div>
      <span className="t-caption dim">
        Quelli della connessione Pushover: si cambiano in Strumenti.
      </span>
    </div>
  );
}
